'use client';

import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  useProduct,
  useBundleSelection
} from '@/components/product/product-context';
import YotpoScript from './YotpoScript';
import { productDecode } from '@/lib/shopify/gid';

const YOTPO_INSTANCE_ID = '456518';

export default function RatingsReviews({ block, position }) {
  const { title, subtitle, uid } = block.ratings_and_reviews.reference[0];
  const { productData, settings, isBundlePage } = useProduct();
  const bundleState = useBundleSelection();
  const [remountKey, setRemountKey] = useState(0);

  // Bump the key to remount Yotpo on Activity show, since Yotpo won't
  // re-initialize when restored from Activity cache.
  // TODO: Replace with useRouter().bfcacheId key once we upgrade to Next.js 16.3+
  useLayoutEffect(() => {
    return () => {
      setRemountKey((key) => key + 1);
    };
  }, []);

  const url =
    process.env.NEXT_PUBLIC_NODE_ENV === 'development'
      ? 'beta.leesa.com'
      : 'www.leesa.com';

  // Find the Shopify product within the bundle or normal product data
  let shopifyProduct = null;
  if (isBundlePage) {
    shopifyProduct = bundleState.currentOffers.find(
      (offer) => offer.settings.reviews_product
    )?.product;
  } else {
    shopifyProduct = productData?.shopifyProduct;
  }

  // Don't show anything if there's no Shopify product
  if (!shopifyProduct) return null;

  // Handle overrides on product id from settings
  const overrideProductId = settings?.product_review_stars_override;
  const productId = overrideProductId ?? productDecode(shopifyProduct.id)?.id;

  return (
    <section id={`${uid}_${position}`} data-name="ratings-reviews-section">
      <div className="standard-padding max-w-container mx-auto">
        <div className="flex flex-col items-center px-5 text-center sm:px-15 lg:px-20">
          <h2 className="h4 sm:h2 mb-6" id="reviewTitle">
            {title}
          </h2>
          <p className="body-r sm:sh8 max-w-xl px-2 pb-5 lg:pb-15">
            {subtitle}
          </p>
        </div>
        <YotpoScript />
        <YotpoWidget
          key={remountKey}
          productId={productId}
          title={shopifyProduct.title}
          handle={shopifyProduct.handle}
          imageUrl={shopifyProduct.images?.[0]?.src || ''}
          url={url}
        />
      </div>
    </section>
  );
}

function YotpoWidget({ productId, title, handle, imageUrl, url }) {
  const divRef = useRef(null);

  useEffect(() => {
    // If you use initWidgets (plural), as of this writing it causes a stale
    // render during client navigation.
    window.yotpoWidgetsContainer?.initWidget?.(
      YOTPO_INSTANCE_ID,
      divRef.current
    );
  }, [productId]);

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 640px)');
    const handler = () =>
      window.yotpoWidgetsContainer?.initWidget?.(
        YOTPO_INSTANCE_ID,
        divRef.current
      );
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  return (
    <div
      ref={divRef}
      className="yotpo-widget-instance"
      data-yotpo-instance-id={YOTPO_INSTANCE_ID}
      data-yotpo-product-id={productId}
      data-yotpo-name={title}
      data-yotpo-url={`https://${url}/products/${handle}`}
      data-yotpo-image-url={imageUrl}
      data-yotpo-currency="USD"
      data-yotpo-description={title}
    ></div>
  );
}
